import { Auth0Provider } from '@auth0/auth0-react';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import ReactDOM from 'react-dom/client';
import { App } from './app';
import { DemoAuthProvider, isDemoMode } from './auth/demo-auth-provider';
import { EVENTS, track } from './helpers/analytics';
import { keepLogsEvent, keepRumEvent } from './helpers/datadog-filters';
import { captureUtm, getUtm, utmProps } from './helpers/utm';
import { router } from './router';

import './index.css';

// Capture UTM attribution on the app/demo domain. localStorage so it survives
// the Auth0 round-trip (see the /signup route). On the demo domain, a bare
// demo view (no further clicks) is still attributable via a landing event —
// Plausible auto-pageviews do not include the non-standard utm_lead prop.
captureUtm('local');
if (isDemoMode()) {
  const utm = getUtm();
  if (Object.keys(utm).length > 0) {
    track(EVENTS.demoLanding, utmProps(utm));
  }
}

if (import.meta.env.MODE === 'production') {
  datadogRum.init({
    applicationId: import.meta.env.VITE_DATADOG_APP_ID,
    clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: import.meta.env.VITE_APP_DOMAIN,
    env: import.meta.env.VITE_APP_ENV,
    version: import.meta.env.VITE_APP_VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'allow',
    beforeSend: keepRumEvent,
  });

  datadogLogs.init({
    clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: import.meta.env.VITE_APP_DOMAIN,
    env: import.meta.env.VITE_APP_ENV,
    version: import.meta.env.VITE_APP_VERSION,
    forwardConsoleLogs: ['log', 'warn', 'error'],
    sessionSampleRate: 100,
    beforeSend: keepLogsEvent,
  });
}

const domNode = document.querySelector('#app-root') as Element;
const root = ReactDOM.createRoot(domNode);

const authProvider = isDemoMode() ? (
  <DemoAuthProvider>
    <App />
  </DemoAuthProvider>
) : (
  <Auth0Provider
    domain={import.meta.env.VITE_AUTH0_DOMAIN}
    clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
    cacheLocation="localstorage"
    cookieDomain=".liquorstore.tech"
    // Renew tokens via the refresh-token grant (direct POST /oauth/token)
    // instead of a hidden-iframe prompt=none /authorize call. The iframe path
    // depends on third-party cookies (broken by Safari ITP, Brave, Chrome
    // Tracking Protection) and hits the /authorize consent gate on
    // non-verifiable callbacks like localhost — both surface as recurring
    // "Login/Consent required" dead-ends. Requires refresh-token rotation on
    // the client and offline_access on the API (both already provisioned).
    //
    // Fallback is ON, and it is load-bearing for the rollout: every session
    // that predates offline_access has no refresh token cached, so the first
    // renewal after deploy would otherwise throw MissingRefreshTokenError and
    // hard-break the entire active user base. With the fallback, that first
    // renewal falls back to the iframe once, the code exchange returns a
    // refresh token (offline_access is in scope), and every subsequent renewal
    // uses the refresh-token grant. The fallback is inert once a user has a
    // rotating refresh token, so it is a migration bridge, not a permanent
    // dependence on the cookie-based iframe path.
    useRefreshTokens={true}
    useRefreshTokensFallback={true}
    authorizationParams={{
      redirect_uri: window.location.origin,
      audience: import.meta.env.VITE_AUTH0_AUDIENCE,
      scope: 'openid profile email offline_access',
    }}
    onRedirectCallback={(appState) => {
      // Use router.history to update the URL before RouterProvider renders.
      // This preserves deep-links (appState.returnTo) and avoids the
      // / → /stores/$storeId redirect chain. Safe to call here because
      // the isAuthProcessing gate in App ensures RouterProvider hasn't
      // mounted yet, so there are no history subscribers.
      router.history.push(appState?.returnTo || '/');
    }}
  >
    <App />
  </Auth0Provider>
);

root.render(authProvider);

function removeServiceWorker() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker
      .getRegistrations()
      .then((registrations) => {
        for (const registration of registrations) {
          console.log('Unregistering service worker: ', registration);
          registration.unregister();
          console.log('Refreshing page');
          window.location.reload();
        }
      })
      .catch((error) => {
        console.log('Service Worker unregistration failed: ', error);
      });
  }
}

removeServiceWorker();
